import { Injectable, inject } from '@angular/core';
import {
  AlertController,
  IonicSafeString,
  ToastController
} from '@ionic/angular';
import { SharedUtilsService } from './utils.service';
import { getThemeAwareColor } from '../utils/contrast-utils';

/**
 * Configuration interface for a SimpleAlert
 */
interface SimpleAlertConfig {
  /**
   * ID of the alert (if we want to remove the popup later)
   */
  id?: string;
  /**
   * Color of the alert
   */
  color?: string;
  /**
   * Title of the alert
   */
  title?: string;
  /**
   * Message of the alert
   */
  message?: IonicSafeString | string;
  /**
   * An array of button labels
   */
  buttons?: Array<string>;
  /**
   * Flag for Network Error
   */
  isNetworkError?: string;
  /**
   * Assign custom class to the alert component
   * for styling
   */
  alertClass?: string;
}

@Injectable()
export class SharedPopupService {
  private utilsService = inject(SharedUtilsService);
  private alertController = inject(AlertController);
  private toastController = inject(ToastController);

  /**
   * @ignore
   */
  private registeredPopup: { [id: string]: HTMLIonAlertElement } = {};

  /**
   * If a popup exists / is registered
   *
   * @param id The ID of the popup
   *
   * @return If the popup exists / is registered
   */
  exist(id: string): boolean {
    return (id && !!this.registeredPopup[id]) || false;
  }

  /**
   * Dismiss a registered popup
   *
   * @param id The ID of the popup
   */
  dismiss(id: string): void {
    if (this.exist(id)) {
      this.registeredPopup[id].dismiss();
      delete this.registeredPopup[id];
    }
  }

  /**
   * Dismiss all registered popups
   */
  dismissAll(): void {
    Object.keys(this.registeredPopup).forEach(id => this.dismiss(id));
  }

  /**
   * Get the current theme
   * @returns 'light' | 'dark'
   */
  getTheme(): 'light' | 'dark' {
    const theme = localStorage.getItem('bresearch-theme') ?? 'light';

    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }

    return theme === 'dark' ? 'dark' : 'light';
  }

  /**
   * Display a simple alert. If the ID is already used, the previous popup will dismiss.
   *
   * @param config Alert configuration, see [SimpleAlertConfig]{@link #SimpleAlertConfig}
   *
   * @returns A Promise of with the index of the clicked button
   */
  simpleAlert(config: SimpleAlertConfig): Promise<number> {
    if (config.id) {
      this.dismiss(config.id as string);
    }
    return new Promise<number>(resolve => {
      this.alertController
        .create({
          mode: 'ios',
          cssClass: config.alertClass
            ? `bepatient-shared-alert ${config.alertClass}`
            : 'bepatient-shared-alert',
          backdropDismiss: false,
          header: config.title,
          message: config.message,
          buttons: config.buttons
            ? config.buttons.map((button, index, array) => ({
                text: button,
                handler: () => {
                  if (config.isNetworkError) {
                    this.toastController
                      .create({
                        id: 'network-toast',
                        message: config.isNetworkError,
                        position: 'top',
                        cssClass: 'bepatient_dynamic_form_save_toast',
                        buttons: [
                          {
                            icon: 'close',
                            role: 'cancel',
                            handler: () => {
                              // Close button handler
                            }
                          }
                        ]
                      })
                      .then(toast => toast.present());
                  }
                  return resolve(index);
                },
                cssClass:
                  array.length <= 1
                    ? 'bepatient-shared-alert-single-button'
                    : index === array.length - 1
                    ? 'bepatient-shared-alert-last-button'
                    : 'bepatient-shared-alert-button'
              }))
            : []
        })
        .then(alert => {
          alert.present();
          if (config.id) {
            localStorage.setItem('sessionPopup', config.id);
            this.dismiss(config.id);
            alert
              .onDidDismiss()
              .then(() => {
                if (config.id) {
                  delete this.registeredPopup[config.id as string];
                }
              })
              .catch(() => {});
            this.registeredPopup[config.id] = alert;
            const popupLength = Object.keys(this.registeredPopup).length;
            if (popupLength > 1) {
              this.dismissAll();
            }
          }
          if (config.color) {
            // Theme-adjust the study color so the alert accent stays legible
            // against the current theme background, then derive its contrast
            // text color from the adjusted value. Both flow through the
            // canonical WCAG-AA utility — see
            // docs/adr/0001-contrast-utility-consolidation.md.
            const themedColor = getThemeAwareColor(config.color);
            alert.style.setProperty(
              '--bepatient-shared-alert-color',
              themedColor
            );
            alert.style.setProperty(
              '--bepatient-shared-alert-color-contrast',
              this.utilsService.contrastColor(themedColor)
            );
          }
        });
    });
  }
}
